<script setup lang="ts">
import { useFlowControlStore } from '@components/flow-controller'
import { IconExclamationCircle } from '@tabler/icons-vue'
import { computed, ref, watch } from 'vue'

import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import type { Schema } from '@tix/openapi-sdk'

import { getErrorForAction } from '@/helpers/flowActionErrors'
import { formatVariantQuantityValue } from '@/helpers/flowStepValue'

import { useBundleStepTracking } from '@/composables/useBundleStepTracking'

import VariantRow from '../../VariantRow.vue'

import ActionHeader from './ActionHeader.vue'
import type { ActionValidationError } from './types'

const actionType: Schema<'action_pick_variants'>['type'] = 'pick_variants'

// State

const store = useFlowControlStore()
const { trackBundleStep } = useBundleStepTracking()

const action = computed(() => (store.action?.type === actionType && store.action) || null)

const variants = computed(() => action.value?.options?.variants ?? [])

type VariantName = (typeof variants.value)[number]['variant']
/* Local state format. E.g. { "Adult": 2, "Child": 1 } */
type Quantities = Record<VariantName, number>
/* API state format. E.g. ["Adult", "Adult", "Child"] */
type InputVariants = VariantName[]

// Start with all variants at their default (0 or min)
const defaultQuantities: Quantities = Object.fromEntries(variants.value.map((v) => [v.variant, v.min ?? 0]))

// Initialize from previous input if available (e.g. when navigating back)
const prevInput = store.currentActionPreviousInput
const prevInputVariants: InputVariants = (prevInput?.type === actionType && prevInput.input?.variants) || []

// Substitute counted values from previous input (if any)
// NOTE: We assume the API data is consistent, i.e. previous input's match the current action's variants.
const quantities = ref<Quantities>({
  ...defaultQuantities,
  // Transform ["Adult", "Adult", "Child"] → { "Adult": 2, "Child": 1 }
  ...prevInputVariants.reduce((acc, variant) => ({ ...acc, [variant]: (acc[variant] || 0) + 1 }), {} as Quantities),
})

const validationError = ref<ActionValidationError | null>(null)
const title = computed<string | undefined>(() => action.value?.meta?.summary_title)
const prompt = computed<string | undefined>(() => action.value?.meta?.prompt)

// TODO - Improve wording of user-facing error messaging - https://ticketure-team.atlassian.net/browse/TIC-5452
const errorMessage = computed<string | null>(() => {
  if (!action.value) return `Action is not a ${actionType} action`
  if (validationError.value) return validationError.value.message ?? 'Validation error'
  return null
})

// Reset error on user input
watch(quantities, () => (validationError.value = null), { deep: true })

/** Per +/- click — no-op when the picker clamps at min/max. */
function handleQuantityChange(variant: VariantName, quantity: number): void {
  if (quantities.value[variant] === quantity) return

  quantities.value[variant] = quantity
  trackBundleStep(formatVariantQuantityValue(variant, quantity))
}

// Store Hooks

store.registerInputGetter(() => {
  // Transform { "Adult": 2, "Child": 1 } → ["Adult", "Adult", "Child"]
  const inputVariants: InputVariants = Object.entries(quantities.value).flatMap(([variant, count]) =>
    Array<VariantName>(count).fill(variant),
  )
  const totalSelected = inputVariants.length
  const minTotal = action.value?.options?.min ?? 1
  const maxTotal = action.value?.options?.max ?? Infinity

  const isValid = totalSelected >= minTotal && totalSelected <= maxTotal

  if (!isValid) {
    const message =
      maxTotal === Infinity
        ? `Please select at least ${minTotal}.`
        : `Please select between ${minTotal} and ${maxTotal} total.`

    validationError.value = { type: 'client-side', message }
    return { isValid: false }
  }

  // Reset errors on every submission attempt
  validationError.value = null

  return { isValid: true, input: { variants: inputVariants } }
})

store.registerErrorHandler((apiError: unknown) => {
  const { message } = getErrorForAction(apiError, actionType)
  if (message) validationError.value = { type: 'server-side', message }

  return Boolean(message)
})
</script>

<template>
  <section class="pick-variants-action">
    <template v-if="action">
      <ActionHeader :description="prompt" :title="title" />

      <TransitionGroup appear class="items" name="transition-group-item" tag="div">
        <VariantRow
          v-for="(variant, index) in variants"
          :key="variant.variant"
          :description="variant.description"
          :max="variant.max"
          :min="variant.min"
          :modelValue="quantities[variant.variant]"
          :name="variant.variant"
          :style="{ '--index': index }"
          @update:modelValue="handleQuantityChange(variant.variant, $event)"
        />
      </TransitionGroup>
    </template>

    <StatusMessage :icon="IconExclamationCircle" :message="errorMessage" variant="error" />
  </section>
</template>

<style scoped>
.pick-variants-action {
  display: flex;
  flex-direction: column;

  .items {
    display: flex;
    flex-direction: column;
  }

  .transition-group-item-enter-active {
    transition:
      opacity 0.4s ease,
      transform 0.4s ease;
    transition-delay: calc(180ms * (var(--index, 0) / (var(--index, 0) + 2)));
  }
  .transition-group-item-enter-from {
    opacity: 0;
    transform: translateY(30px);
  }
}
</style>
